/**
 * Plain-text editing format for the rich-text editor (task W9). People type simple marks in a normal text box
 * and the editor stores the restricted HTML subset of ADR 0020 (p, br, strong, em, u, a[href], ul, ol, li, h2, h3,
 * blockquote, plus {{merge}} tokens). The server sanitises again; it never trusts this.
 *
 *   blank line = new paragraph    **bold**   *italic*   ++underline++   [text](https://...)
 *   "- " bullet   "1. " numbered   "## " heading   "### " small heading   "> " quote   \ escapes a mark
 */

const SAFE_HREF = /^(https?:|mailto:|tel:)/i;
const STASH = '';

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function isSafeHref(href: string): boolean {
  return SAFE_HREF.test(href.trim());
}

function inlineToHtml(src: string): string {
  const stash: string[] = [];
  const keep = (html: string) => `${STASH}${stash.push(html) - 1}${STASH}`;
  let s = src.replace(/\\([\s\S])/g, (_m, ch: string) => keep(escapeHtml(ch)));
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text: string, href: string) =>
    isSafeHref(href) ? keep(`<a href="${escapeHtml(href.trim())}">${escapeHtml(text)}</a>`) : escapeHtml(text),
  );
  s = escapeHtml(s);
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\+\+(.+?)\+\+/g, '<u>$1</u>').replace(/\*(.+?)\*/g, '<em>$1</em>');
  const restore = new RegExp(`${STASH}(\\d+)${STASH}`, 'g');
  return s.replace(restore, (_m, i: string) => stash[Number(i)] ?? '');
}

/** Converts the editing format to the stored HTML subset. */
export function markupToHtml(markup: string): string {
  const lines = markup.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let para: string[] = [];
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length) out.push(`<p>${para.map(inlineToHtml).join('<br>')}</p>`);
    para = [];
  };
  const flushList = () => {
    if (list) out.push(`<${list.tag}>${list.items.map((i) => `<li>${inlineToHtml(i)}</li>`).join('')}</${list.tag}>`);
    list = null;
  };
  for (const line of lines) {
    const bullet = line.match(/^- (.*)$/);
    const numbered = line.match(/^\d+\. (.*)$/);
    const heading = line.match(/^(##|###) (.*)$/);
    const quote = line.match(/^> ?(.*)$/);
    if (line.trim() === '') {
      flushPara();
      flushList();
    } else if (bullet || numbered) {
      flushPara();
      const tag = bullet ? 'ul' : 'ol';
      if (list && list.tag !== tag) flushList();
      list ??= { tag, items: [] };
      list.items.push((bullet ?? numbered)?.[1] ?? '');
    } else if (heading) {
      flushPara();
      flushList();
      const tag = heading[1] === '##' ? 'h2' : 'h3';
      out.push(`<${tag}>${inlineToHtml(heading[2] ?? '')}</${tag}>`);
    } else if (quote) {
      flushPara();
      flushList();
      out.push(`<blockquote>${inlineToHtml(quote[1] ?? '')}</blockquote>`);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return out.join('');
}

function escapeText(text: string): string {
  return text.replace(/([\\*[])/g, '\\$1').replace(/\+\+/g, '\\+\\+');
}

function inlineFromNode(node: Node): string {
  if (node.nodeType === 3) return escapeText(node.textContent ?? '');
  if (node.nodeType !== 1) return '';
  const el = node as Element;
  const inner = Array.from(el.childNodes).map(inlineFromNode).join('');
  switch (el.tagName.toLowerCase()) {
    case 'br':
      return '\n';
    case 'strong':
    case 'b':
      return `**${inner}**`;
    case 'em':
    case 'i':
      return `*${inner}*`;
    case 'u':
      return `++${inner}++`;
    case 'a': {
      const href = el.getAttribute('href') ?? '';
      return isSafeHref(href) ? `[${inner}](${href.trim()})` : inner;
    }
    default:
      return inner;
  }
}

function guardLineStart(line: string): string {
  return /^(- |#{2,3} |>|\d+\. )/.test(line) ? '\\' + line : line;
}

/** Converts stored HTML (any HTML; unknown tags keep their text) to the editing format. */
export function htmlToMarkup(html: string): string {
  if (!html.trim()) return '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const blocks: string[] = [];
  const handle = (el: Node) => {
    if (el.nodeType === 3) {
      const text = escapeText((el.textContent ?? '').trim());
      if (text) blocks.push(text);
      return;
    }
    if (el.nodeType !== 1) return;
    const e = el as Element;
    const tag = e.tagName.toLowerCase();
    if (tag === 'script' || tag === 'style') return;
    const inline = () => Array.from(e.childNodes).map(inlineFromNode).join('');
    if (tag === 'ul' || tag === 'ol') {
      const items = Array.from(e.children).filter((c) => c.tagName.toLowerCase() === 'li');
      blocks.push(
        items
          .map((li, i) => `${tag === 'ul' ? '- ' : `${i + 1}. `}${Array.from(li.childNodes).map(inlineFromNode).join('').replace(/\n/g, ' ')}`)
          .join('\n'),
      );
    } else if (tag === 'h2') blocks.push(`## ${inline()}`);
    else if (tag === 'h3') blocks.push(`### ${inline()}`);
    else if (tag === 'blockquote') blocks.push(`> ${inline().replace(/\n/g, ' ')}`);
    else if (tag === 'p') blocks.push(inline().split('\n').map(guardLineStart).join('\n'));
    else if (['strong', 'b', 'em', 'i', 'u', 'a', 'br'].includes(tag)) blocks.push(inlineFromNode(e));
    else Array.from(e.childNodes).forEach(handle);
  };
  Array.from(doc.body.childNodes).forEach(handle);
  return blocks.filter((b) => b !== '').join('\n\n');
}

/** Keeps only the allowed subset: round-trips through the editing format. Use before showing stored HTML in a preview. */
export function normaliseRichHtml(html: string): string {
  return markupToHtml(htmlToMarkup(html));
}

/** Fills {{path}} tokens with escaped sample values; unknown paths render empty (matches the server's renderTemplate). */
export function fillMergeFields(html: string, values: Readonly<Record<string, string>>, opts: { html: boolean } = { html: true }): string {
  return html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, path: string) => {
    const v = values[path] ?? '';
    return opts.html ? escapeHtml(v) : v;
  });
}
